import {
  Button,
  Flex,
  Heading,
  Select,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FC, useCallback, useEffect, useState } from 'react';
import Pagination from '../../../../components/Pagination';
import { ENV } from '../../../../constants';
import {
  apagarArquivo,
  apagarNaoUsados,
  buscarResumo,
  listarArquivos,
} from '../../api';
import ModalApagar from '../../components/ModalApagar';
import ResumoCards from '../../components/ResumoCards';
import TabelaArquivos from '../../components/TabelaArquivos';
import {
  ArquivoDeMidia,
  ArquivoEmUsoNoLote,
  OrdenacaoArquivos,
  PaginaDeArquivos,
  ResumoDeMidia,
  SituacaoArquivo,
} from '../../models';
import { nomeDoTenant } from '../../utils';

const POR_PAGINA = 20;
const ABAS: SituacaoArquivo[] = ['em-uso', 'nao-usado'];

// aba selecionada com sublinhado forte; a não selecionada fica só como texto
const ESTILO_ABA = {
  fontWeight: 'medium',
  color: 'gray.500',
  borderBottomWidth: '3px',
  _selected: { color: '#469BFF', borderColor: '#469BFF', fontWeight: 'bold' },
};

const mensagemDeErro = (erro: any): string =>
  erro?.response?.data?.message ||
  erro?.message ||
  'Tivemos um erro, tente novamente mais tarde';

const List: FC = () => {
  const toast = useToast();

  const [situacao, setSituacao] = useState<SituacaoArquivo>('em-uso');
  const [ordenarPor, setOrdenarPor] = useState<OrdenacaoArquivos>('peso');
  const [pagina, setPagina] = useState(1);
  const [resumo, setResumo] = useState<ResumoDeMidia | null>(null);
  const [lista, setLista] = useState<PaginaDeArquivos | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [paraApagar, setParaApagar] = useState<ArquivoDeMidia[]>([]);
  const [apagando, setApagando] = useState(false);

  const avisarErro = useCallback(
    (descricao: string) =>
      toast({
        title: 'Opss!!',
        description: descricao,
        status: 'error',
        position: 'top-right',
      }),
    [toast],
  );

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [novoResumo, novaLista] = await Promise.all([
        buscarResumo(),
        listarArquivos({
          situacao,
          pagina,
          porPagina: POR_PAGINA,
          ordenarPor,
        }),
      ]);
      setResumo(novoResumo);
      setLista(novaLista);
    } catch (erro) {
      avisarErro(mensagemDeErro(erro));
    } finally {
      setCarregando(false);
    }
  }, [situacao, pagina, ordenarPor, avisarErro]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    setSelecionados(new Set());
  }, [situacao, pagina, ordenarPor]);

  const trocarAba = (indice: number) => {
    setSituacao(ABAS[indice]);
    setOrdenarPor(ABAS[indice] === 'nao-usado' ? 'mais-antigas' : 'peso');
    setPagina(1);
  };

  const selecionar = (id: string, selecionado: boolean) =>
    setSelecionados(atuais => {
      const novos = new Set(atuais);
      if (selecionado) novos.add(id);
      else novos.delete(id);
      return novos;
    });

  const selecionarTodos = (selecionado: boolean) =>
    setSelecionados(
      selecionado ? new Set(lista?.dados.map(({ id }) => id)) : new Set(),
    );

  const confirmarExclusao = async () => {
    setApagando(true);
    try {
      if (paraApagar.length === 1) {
        await apagarArquivo(paraApagar[0].id);
      } else {
        await apagarNaoUsados(paraApagar.map(({ id }) => id));
      }
      toast({
        title: 'Sucesso!',
        description:
          paraApagar.length === 1
            ? 'Imagem apagada.'
            : `${paraApagar.length} imagens apagadas.`,
        status: 'success',
        position: 'top-right',
      });
      setParaApagar([]);
      setSelecionados(new Set());
      // a página pode ter ficado vazia depois de apagar
      if (lista && lista.dados.length === paraApagar.length && pagina > 1) {
        setPagina(pagina - 1);
      } else {
        await carregar();
      }
    } catch (erro: any) {
      const emUso: ArquivoEmUsoNoLote[] | undefined =
        erro?.response?.data?.emUso;
      avisarErro(
        emUso?.length
          ? `Nada foi apagado: ${emUso
              .map(({ nomeOriginal }) => nomeOriginal)
              .join(', ')} passou(aram) a ser usada(s). Atualize a lista.`
          : mensagemDeErro(erro),
      );
      setParaApagar([]);
      await carregar();
    } finally {
      setApagando(false);
    }
  };

  const arquivosSelecionados =
    lista?.dados.filter(({ id }) => selecionados.has(id)) ?? [];

  return (
    <Flex
      flexDir="column"
      w="100%"
      p={8}
      overflow="auto"
      backgroundColor="#F9F9F9"
      // sem isso o flex encolhe a tabela para caber na altura da tela e a página não rola
      sx={{ '& > *': { flexShrink: 0 } }}
      css={{
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { width: '6px' },
        '&::-webkit-scrollbar-thumb': {
          background: '#469BFF',
          borderRadius: '24px',
        },
      }}
    >
      <Heading mb={2} color="#469BFF" fontFamily="Poppins">
        Diagnóstico de mídia
      </Heading>
      <Text mb={8} color="gray.600">
        {nomeDoTenant(ENV.REACT_APP_TENANT_ID)} · peso e uso das imagens
        publicadas no app
      </Text>

      <ResumoCards resumo={resumo} />

      <Flex align="center" justify="space-between" mb={4} flexWrap="wrap">
        <Tabs
          index={ABAS.indexOf(situacao)}
          onChange={trocarAba}
          variant="line"
          colorScheme="blue"
        >
          <TabList borderBottom="none">
            <Tab {...ESTILO_ABA}>Em uso</Tab>
            <Tab {...ESTILO_ABA}>Não usadas</Tab>
          </TabList>
        </Tabs>

        <Flex align="center" gap={3}>
          {situacao === 'nao-usado' && (
            <Button
              colorScheme="red"
              size="sm"
              isDisabled={!arquivosSelecionados.length}
              onClick={() => setParaApagar(arquivosSelecionados)}
            >
              {`Apagar selecionadas (${arquivosSelecionados.length})`}
            </Button>
          )}
          <Select
            size="sm"
            w="200px"
            bg="white"
            value={ordenarPor}
            onChange={evento => {
              setOrdenarPor(evento.target.value as OrdenacaoArquivos);
              setPagina(1);
            }}
            aria-label="Ordenar por"
          >
            <option value="peso">Mais pesadas</option>
            <option value="mais-antigas">Mais antigas</option>
            <option value="mais-recentes">Mais recentes</option>
          </Select>
        </Flex>
      </Flex>

      {lista && !lista.dados.length && !carregando && (
        <Text mx="auto" my={10} opacity="0.8">
          Nenhuma imagem encontrada
        </Text>
      )}

      {lista && !!lista.dados.length && (
        <TabelaArquivos
          arquivos={lista.dados}
          permiteSelecao={situacao === 'nao-usado'}
          selecionados={selecionados}
          onSelecionar={selecionar}
          onSelecionarTodos={selecionarTodos}
          onApagar={arquivo => setParaApagar([arquivo])}
        />
      )}

      {lista && lista.total > POR_PAGINA && (
        <Pagination
          ml="auto"
          mt={6}
          disabled={carregando}
          total={lista.total}
          current={pagina}
          perPage={POR_PAGINA}
          onPageChange={setPagina}
        />
      )}

      {carregando && (
        <Flex
          position="absolute"
          align="center"
          justify="center"
          top="50%"
          left="50%"
        >
          <Spinner color="brand.500" size="xl" />
        </Flex>
      )}

      <ModalApagar
        arquivos={paraApagar}
        isOpen={paraApagar.length > 0}
        carregando={apagando}
        onClose={() => setParaApagar([])}
        onConfirm={confirmarExclusao}
      />
    </Flex>
  );
};

export default List;
